import {
  type LiveOpenShipment,
  type LivePackingSlip,
  LiveRefused,
  type LiveStoreShipping,
} from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  buyShipmentLabel,
  loadStoreShipping,
  markShipmentShipped,
  packingSlip,
  setShipmentAddress,
  shippingCsv,
} from "../../core/ops/live-ops";
import { saveCsv } from "../../core/primitives/download";
import { errorText } from "../../core/primitives/errors";
import { busyForm, failedForm, idleForm } from "../../core/primitives/load-status";
import { Loader } from "../../core/primitives/loader";
import {
  type AddressDraft,
  addressDraft,
  type ShipmentMove,
  ShippingActions,
  type ShippingFilter,
  ShippingState,
} from "./shipping-model";

export type ShippingDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

/**
 * The Shipping tab: the parcels, and money owed back.
 *
 * A finished box's spots go straight into the buyer's open parcel, across every
 * stream, until its label is bought. One person, one envelope, one label.
 */
export class ShippingController extends SubtreeController implements ShippingActions {
  readonly state = new ShippingState();
  readonly #list: Loader<string, LiveStoreShipping>;
  readonly #slip: Loader<string, LivePackingSlip>;
  #shopId: string | null = null;

  constructor(readonly deps: ShippingDeps) {
    super();
    this.subtree.put(ShippingState, this.state);
    this.subtree.put(ShippingActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (shopId: string) => loadStoreShipping({ ...deps, shopId, locationId: deps.storeRepository.locationId }),
        loaded: (data) => {
          this.state.shipments.value = data.shipments;
          this.state.refundsOwed.value = data.refundsOwed;
          this.state.carrierShipping.value = data.carrierShipping;
          this.state.shippingNote.value = data.shippingNote;
        },
      }),
    );
    this.#slip = this.own(
      new Loader({
        status: this.state.slipStatus,
        fetch: (shipmentId: string) => packingSlip({ ...deps, shipmentId }),
        loaded: (slip, shipmentId) => {
          if (this.state.slipShipmentId.value === shipmentId) this.state.slip.value = slip;
        },
      }),
    );
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      this.state.shipments.value = [];
      this.state.refundsOwed.value = [];
      void this.reload();
    }, [deps.storeRepository]);
  }

  setFilter = (filter: ShippingFilter) => {
    this.state.filter.value = filter;
  };

  reload = async (): Promise<void> => {
    if (this.#shopId) await this.#list.load(this.#shopId);
  };

  exportCsv = async () => {
    const shopId = this.#shopId;
    if (!shopId || this.state.csv.value.busy) return;
    this.state.csv.value = busyForm;
    const r = await shippingCsv({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.csv.value = failedForm(r.errors);
      return;
    }
    // The server names the file.
    saveCsv(r.value.filename, r.value.csv);
    this.state.csv.value = idleForm;
  };

  editAddress = (shipment: LiveOpenShipment) => {
    this.state.editing.value = shipment.shipmentId;
    this.state.address.value = addressDraft(shipment.address);
    this.state.addressMessages.value = [];
    this.state.addressSave.value = idleForm;
  };

  closeAddress = () => {
    this.state.editing.value = null;
    void this.reload();
  };

  setAddressField = (key: keyof AddressDraft, value: string) => {
    this.state.address.update((a) => ({ ...a, [key]: value }));
  };

  saveAddress = async () => {
    const shipmentId = this.state.editing.value;
    if (!shipmentId || this.state.addressSave.value.busy) return;
    const f = this.state.address.value;
    this.state.addressSave.value = busyForm;
    const r = await setShipmentAddress({
      ...this.deps,
      shipmentId,
      address: {
        name: f.name.trim(),
        street1: f.street1.trim(),
        street2: f.street2.trim() || null,
        city: f.city.trim(),
        state: f.state.trim() || null,
        zip: f.zip.trim() || null,
        country: f.country.trim().toUpperCase(),
        phone: f.phone.trim() || null,
      },
    });
    if (this.isDisposed || this.state.editing.value !== shipmentId) return;
    if (!r.ok) {
      this.state.addressSave.value = failedForm(r.errors);
      return;
    }
    this.state.addressSave.value = idleForm;
    // The carrier's opinion is advisory: a remark is shown and the form stays open until Done; nothing to say closes it.
    this.state.addressMessages.value = r.value.messages;
    if (r.value.messages.length === 0) this.closeAddress();
  };

  buyLabel = async (shipmentId: string) => {
    if (this.state.rowBusy.value) return;
    const prompted = this.state.phonePrompt.value === shipmentId;
    const senderPhone = prompted ? this.state.senderPhone.value.trim() : "";
    if (prompted && !senderPhone) return;
    const errors = await this.#row(shipmentId, "label", () =>
      buyShipmentLabel({ ...this.deps, shipmentId, senderPhone: senderPhone || undefined }),
    );
    if (this.isDisposed) return;
    if (!errors) {
      this.state.phonePrompt.value = null;
      this.state.senderPhone.value = "";
    } else if (errors.some((e) => e instanceof LiveRefused && e.code === "no_ship_from_phone")) {
      this.state.phonePrompt.value = shipmentId;
    }
  };

  setSenderPhone = (value: string) => {
    this.state.senderPhone.value = value;
  };

  markPosted = async (shipmentId: string) => {
    await this.#row(shipmentId, "shipped", () => markShipmentShipped({ ...this.deps, shipmentId }));
  };

  openSlip = (shipmentId: string) => {
    this.state.slipShipmentId.value = shipmentId;
    this.state.slip.value = null;
    void this.#slip.load(shipmentId);
  };

  closeSlip = () => {
    this.state.slipShipmentId.value = null;
    this.state.slip.value = null;
    this.#slip.reset();
  };

  /** The errors when the call failed, null when it went through (or never ran). */
  async #row(
    shipmentId: string,
    move: ShipmentMove,
    call: () => Promise<{ ok: true } | { ok: false; errors: readonly Error[] }>,
  ): Promise<readonly Error[] | null> {
    if (this.state.rowBusy.value) return null;
    this.state.rowBusy.value = { shipmentId, move };
    this.state.rowError.value = null;
    const r = await call();
    if (this.isDisposed) return null;
    this.state.rowBusy.value = null;
    if (!r.ok) {
      this.state.rowError.value = { shipmentId, message: errorText(r.errors) };
      return r.errors;
    }
    await this.reload();
    return null;
  }
}
